import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { DialogActions, DialogContent, Button, Typography } from '@material-ui/core';
import { useQuery } from '@apollo/client';
import _get from 'lodash/get';
import _values from 'lodash/values';
import { useFormik } from 'formik';

import { getFormikAutocompleteProps, checkSubmitIsDisabled } from '../form/helpers';
import { removeEmptyProperties, validateAutocompleteValue } from '../form/validate';
import { DUPLICATE_VALIDATED_PROPERTY_ITEMS } from '../../graphql/query/propertyItemDuplicates';

import Loader from '../common/loader';
import PropertyFieldAutocomplete from '../form/property-field/property-field-autocomplete';
import MergeConfirmPopup from '../merge-confirm-popup';

const useStyles = makeStyles((theme) => ({
  root: {
    width: theme.spacing(60),
    maxWidth: theme.spacing(60),
  },
  description: {
    margin: theme.spacing(2, 0, 1),
  },
  error: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
  },
}));

function validate(values) {
  const errors = {};
  const { replacing, target } = values;
  const replacingValue = replacing?.value;
  const targetValue = target?.value;
  const valuesAreSame = replacingValue && targetValue && targetValue === replacingValue;

  errors.replacing = validateAutocompleteValue(replacing, {
    required: true,
    requiredMessage: 'Replacing is required',
  });
  errors.target = validateAutocompleteValue(target, {
    required: true,
    requiredMessage: 'Target is required',
  });
  errors.sameValue = valuesAreSame && "Items mustn't be the same";

  return removeEmptyProperties(errors);
}

const ReplaceCollectionTypePropertyItemForm = (props) => {
  const { error, onSubmit, loading, onClose, initialValues, property } = props;
  const classes = useStyles();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [mergeDupsChecked, setMergeDupsChecked] = useState(false);
  const replacingProperty = { ...property, name: 'replacing' };
  const targetProperty = { ...property, name: 'target' };

  const formik = useFormik({
    onSubmit,
    initialValues,
    validate,
  });
  const { values } = formik;

  const replacing = values?.replacing;
  const target = values?.target;
  const propertyId = property?.id;
  const propertyName = property?.name;

  const { data: duplicatesData } = useQuery(DUPLICATE_VALIDATED_PROPERTY_ITEMS, {
    variables: { propertyId, name: target?.label },
    skip: !confirmOpen || !target?.value,
  });

  // Published-only validated items sharing the target's name, minus the target itself.
  const duplicates = (duplicatesData?.duplicateValidatedPropertyItems || [])
    .filter((item) => item.id !== target?.value)
    .map(({ id, name, itemCount }) => ({ id, name, itemCount }));

  const buttonIsDisabled = checkSubmitIsDisabled(formik) || loading;

  const onOpenConfirm = () => setConfirmOpen(true);

  const onCloseConfirm = () => setConfirmOpen(false);

  const onToggleMergeDups = (e) => setMergeDupsChecked(e?.target?.checked);

  const onApprove = () => {
    const replacingIds = [
      replacing?.value,
      ...(mergeDupsChecked ? duplicates.map((dup) => dup.id) : []),
    ];
    onSubmit({ target_id: target?.value, replacing_ids: replacingIds });
    onCloseConfirm();
  };

  const customFilter = (formikInstance) => (valueObject) => {
    const { value: newValue } = valueObject || {};
    const formValues = formikInstance?.values;
    const valueExists = _values(formValues).find((v) => v?.value && newValue === v?.value);

    return !valueExists;
  };

  const renderError = () => {
    const errorMessage = _get(error, 'errors[0].message');
    if (!errorMessage) return null;
    return <Typography>{errorMessage}</Typography>;
  };

  const autocompleteProps = {
    className: classes.field,
    placeholder: 'Type name',
    showLabel: false,
    customFilter,
    formik,
    canCreateNew: false,
    showOptionId: true,
  };

  return (
    <>
      <DialogContent>
        <div className={classes.root}>
          <Typography className={classes.description}>
            Pick the item which you want replace
          </Typography>
          <PropertyFieldAutocomplete
            id="replacing"
            property={replacingProperty}
            {...autocompleteProps}
            {...getFormikAutocompleteProps({ name: 'replacing', formik })}
          />
          <Typography className={classes.description}>
            Pick the target item (only published)
          </Typography>
          <PropertyFieldAutocomplete
            id="target"
            property={targetProperty}
            {...{ ...autocompleteProps, isPublishedOnly: true }}
            {...getFormikAutocompleteProps({ name: 'target', formik })}
          />
          {renderError()}
        </div>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          Close
        </Button>
        <Button
          onClick={onOpenConfirm}
          disabled={buttonIsDisabled}
          variant="contained"
          color="primary"
        >
          Replace
        </Button>
      </DialogActions>
      <Loader show={loading} absolute />
      {confirmOpen && target?.value && (
        <MergeConfirmPopup
          open={confirmOpen}
          onClose={onCloseConfirm}
          onConfirm={onApprove}
          propertyName={propertyName}
          source={{
            id: replacing?.value,
            name: replacing?.label,
            itemCount: replacing?.itemCount,
          }}
          target={{ id: target?.value, name: target?.label }}
          duplicates={duplicates}
          mergeDupsChecked={mergeDupsChecked}
          onToggleMergeDups={onToggleMergeDups}
        />
      )}
    </>
  );
};

ReplaceCollectionTypePropertyItemForm.propTypes = {
  error: PropTypes.object,
  propertyTypesOptions: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  submitButtonText: PropTypes.string,
  initialValues: PropTypes.object,
  property: PropTypes.object.isRequired,
};

ReplaceCollectionTypePropertyItemForm.defaultProps = {
  error: {},
  propertyTypesOptions: [],
  loading: false,
  submitButtonText: 'Save',
  initialValues: {},
};

export default ReplaceCollectionTypePropertyItemForm;
